/**
 * What a style still owes before it is *finished* (revision item 39).
 *
 * An import — or a hand-written purchase order — creates styles from what the
 * vendor file knows: name, style number, colour, size, cost, MSRP, UPC. It
 * leaves empty everything the file cannot know: the description a customer
 * reads, the retail price where MSRP was missing, the category it is filed in,
 * the bin, the stock targets. This module is the definition of that debt.
 *
 * **Computed, never stored.** A `finished` column would be a cache of this
 * rule, wrong the moment the rule changes — and the rule *will* change
 * (photographs join it in Phase 2, §5.4). Every caller asks; nobody records.
 *
 * The inputs are counts rather than variant rows on purpose: the API computes
 * them in SQL over 13,000 variants and hands this function a dozen integers,
 * so the one implementation serves the banner, the list marker and the guided
 * pass without anyone re-deriving the rule in a query.
 */

/** One thing a style is missing, in the order the guided pass asks for them. */
export type MissingField =
  'category' | 'brand' | 'season' | 'description' | 'retail' | 'bin' | 'targets';

export interface StyleCompleteness {
  /**
   * The category it is filed in, at any level (decision 214). A gender is
   * optional — a helmet has none — so it is not owed and not asked here.
   */
  departmentId: string | null;
  brandId: string | null;
  season: string | null;
  description: string | null;
  /** Services and labour are priced but never counted (§11.2). */
  trackInventory: boolean;
  /** Live variants whose retail is zero — a price nobody set, not a choice. */
  unpricedVariants: number;
  /** Live variants with no bin location. */
  unbinnedVariants: number;
  /** Live variants with no target quantity — OTB is blind to them (§10.3). */
  untargetedVariants: number;
}

/**
 * Everything the style is still missing, in asking order.
 *
 * Empty strings count as missing: a season of `""` answers nothing, and the
 * importer writes `null`, so anything else is a typo with a save button.
 *
 * Brand, season, bin and targets are only owed by styles that track
 * inventory: a ski tune has no maker, no vintage, no shelf and no reorder
 * point, and a debt nobody intends to pay teaches staff to ignore the
 * banner. Retail is owed by everything — a service priced at zero is exactly
 * as unsellable as a jacket priced at zero — and so are the filing and the
 * description, which the receipt and the storefront read.
 */
export function missingFields(style: StyleCompleteness): MissingField[] {
  return fillableGaps(style).filter((field) => !OPTIONAL_FIELDS.includes(field));
}

/**
 * Asked, but not owed (2026-10-02): "we have to set a bin — if we don't have a
 * bin we leave it blank and it should be completed … if we fill out brand and
 * season and target count it should complete." Not every shelf has a bin, and
 * a description is the web shop's want, not the counter's; a style left
 * without them is finished. The pass still offers them, as optional.
 */
export const OPTIONAL_FIELDS: readonly MissingField[] = ['description', 'bin'];

/** Every gap the guided pass can fill — what is owed and what is optional — in asking order. */
export function fillableGaps(style: StyleCompleteness): MissingField[] {
  const missing: MissingField[] = [];
  const blank = (value: string | null) => value === null || value.trim() === '';

  if (style.departmentId === null) missing.push('category');
  if (style.trackInventory && style.brandId === null) missing.push('brand');
  if (style.trackInventory && blank(style.season)) missing.push('season');
  if (blank(style.description)) missing.push('description');
  if (style.unpricedVariants > 0) missing.push('retail');
  if (style.trackInventory && style.unbinnedVariants > 0) missing.push('bin');
  if (style.trackInventory && style.untargetedVariants > 0) missing.push('targets');

  return missing;
}

export function isFinished(style: StyleCompleteness): boolean {
  return missingFields(style).length === 0;
}

/** The words the banner and the marker use — one vocabulary, not per-screen. */
export const MISSING_FIELD_LABELS: Readonly<Record<MissingField, string>> = {
  category: 'category',
  brand: 'brand',
  season: 'season',
  description: 'description',
  retail: 'retail price',
  bin: 'bin',
  targets: 'stock targets',
};
